/**
 * Copyright (C) 2020 Spanning Cloud Apps.  All rights reserved.
 */
'use strict';

/**
 * Module for Intercom services.
 */
angular
  .module('services.intercom', ['services.auth', 'services.billing', 'services.config'])
  .constant('INTERCOM_WIDGET_URL', 'https://widget.intercom.io/widget/')
  .constant('PRODUCT_CODE', 'SFDC')
  .constant('PRODUCT_NAME', 'Salesforce Backup')
  /**
   * Factory used to create Intercom instance
   */
  .provider('IntercomFactory', function () {
    /**
     * Factory to generate Intercom implementation
     * @param $q
     * @param $document
     * @param $window
     * @param billingUtils
     * @param authFactory
     * @param configService
     * @param PRODUCT_CODE
     * @param PRODUCT_NAME
     * @param INTERCOM_WIDGET_URL
     * @returns {{isFeatureEnabled: (function(): *), boot: (function()), shutdown: shutdown}}
     * @constructor
     */
    function IntercomFactory(
      $q,
      $document,
      $window,
      billingUtils,
      authFactory,
      configService,
      PRODUCT_CODE,
      PRODUCT_NAME,
      INTERCOM_WIDGET_URL
    ) {
      let intercomConfig = null;

      const getIntercomConfiguration = function () {
        if (!intercomConfig) {
          return configService.getConfig().then(function (config) {
            intercomConfig = config.intercom;
            return intercomConfig;
          });
        }

        const deferred = $q.defer();
        deferred.resolve(intercomConfig);

        return deferred.promise;
      };

      const mapDataToIntercom = function (userInfo, billingInfo) {
        const user = userInfo.user;
        const organization = userInfo.organization;

        const userId = [PRODUCT_CODE, organization.organizationId, user.email].join('-');

        const isTrial = billingInfo.status === 'TRIAL';
        const customerType = 'Customer';
        const isAdmin = 'Admin';
        const formattedRegion = user.region === 'us' ? user.region.toUpperCase() : 'EMEA';

        return {
          User_ID: userId,
          user_id: userId,
          name: user.firstName + ' ' + user.lastName,
          email: user.email,
          Salesforce_ID: organization.organizationId,
          isAdmin: isAdmin,

          Product: PRODUCT_NAME,
          Company: organization.name,
          Region: formattedRegion,

          IsTrial: isTrial,
          Subscription_Started: billingInfo.starts,
          Subscription_Ends: billingInfo.expires,
          Trial_Started: billingInfo.starts,
          Trial_Ends: billingInfo.expires,
          'Auto-Renew': false,

          Actual_Seats: billingInfo.priceQuote.totalSeats,
          Assigned_Seats: billingInfo.priceQuote.totalSeats,
          Purchased_Seats: billingInfo.paidSeats,
          Payment_Type: billingInfo.type,
          Billing_Contact: billingInfo.email,

          Customer_Type: customerType
        };
      };

      const getAttributes = function () {
        return $q.all([authFactory.getSignedRequest(), billingUtils.getPaymentInfo()]).then(function (results) {
          const userInfo = results[0].context;
          const billingInfo = results[1];

          return mapDataToIntercom(userInfo, billingInfo);
        });
      };

      const insertScript = function (id) {
        const deferred = $q.defer();
        const scriptLoadPromise = deferred.promise;
        let intercom = $window.Intercom;
        if (typeof intercom === 'function') {
          intercom('reattach_activator');
          intercom('update', $window.intercomSettings);
          deferred.resolve();
        } else {
          intercom = function () {
            intercom.c(arguments);
          };

          intercom.q = [];
          intercom.c = function (args) {
            intercom.q.push(args);
          };

          $window.Intercom = intercom;
          const doc = $document[0];
          const script = doc.createElement('script');
          script.type = 'text/javascript';
          script.async = true;
          script.src = INTERCOM_WIDGET_URL + id;

          script.onload = function () {
            deferred.resolve();
          };

          script.onerror = function () {
            deferred.reject();
          };

          doc.head.appendChild(script);
        }

        return scriptLoadPromise;
      };

      const isBooted = function () {
        if ($window.Intercom && $window.Intercom.booted) {
          return true;
        }
        return false;
      };

      const featureDisabledMessage = 'Intercom feature is disabled';
      let waitUntilBootedInterval = null,
        waitUntilBootedPromise = null,
        waitUntilBootedPromiseResolve = null;
      var IntercomFactory = {
        boot: function () {
          getIntercomConfiguration()
            .then(function (intercomConfig) {
              const appId = intercomConfig.appId;
              if (!intercomConfig.enabled) {
                throw new Error(featureDisabledMessage);
              }

              return insertScript(appId).then(function () {
                return appId;
              });
            })
            .then(function (id) {
              const applicationId = id;

              return getAttributes().then(function (attributes) {
                return { attributes, id: applicationId };
              });
            })
            .then(function (payload) {
              const attributes = Object.assign(
                {
                  app_id: payload.id,
                  widget: {
                    activator: '#IntercomDefaultWidget'
                  },
                  vertical_padding: 30
                },
                payload.attributes
              );

              $window.Intercom('boot', attributes);
            });

          return IntercomFactory;
        },
        shutdown: function () {
          $window.Intercom('shutdown');
        },
        isFeatureEnabled: function () {
          return getIntercomConfiguration().then(function (intercomConfig) {
            return intercomConfig.enabled;
          });
        },
        waitUntilBooted() {
          if (isBooted()) {
            return Promise.resolve(true);
          }
          if (waitUntilBootedInterval !== null) {
            return waitUntilBootedPromise;
          }

          waitUntilBootedPromise = new Promise((resolve) => {
            waitUntilBootedPromiseResolve = resolve;
          });

          //will wait 30s
          let attemptsCount = 300;
          waitUntilBootedInterval = setInterval(() => {
            attemptsCount--;
            const isBootedResult = isBooted();
            if (isBootedResult || attemptsCount === 0) {
              clearInterval(waitUntilBootedInterval);
              waitUntilBootedPromiseResolve(isBootedResult);
              waitUntilBootedPromiseResolve = null;
              waitUntilBootedInterval = null;
              waitUntilBootedPromise = null;
            }
          }, 100);
          return waitUntilBootedPromise;
        },
        hide() {
          return this.waitUntilBooted().then((result) => {
            if (result) {
              $window.Intercom('hide');
              $window.Intercom('update', {
                hide_default_launcher: true
              });
            }
            return Promise.resolve(result);
          });
        },
        show() {
          return this.waitUntilBooted().then((result) => {
            if (result) {
              $window.Intercom('update', {
                hide_default_launcher: false
              });
            }
            return Promise.resolve(result);
          });
        }
      };
      return IntercomFactory;
    }

    this.$get = IntercomFactory;
  });
